import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import { dbHelpers } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';

interface Listing {
  id: string;
  business_name: string;
  category: string;
  location: string;
  description: string;
  services: string[];
  phone: string;
  email: string;
  website: string;
  address: string;
  rating: number;
  review_count: number;
  verified: boolean;
  featured: boolean;
  image_url: string;
  hours: any;
  price_range: string;
}

interface Review {
  id: string;
  rating: number;
  title: string;
  comment: string;
  created_at: string;
  profiles: {
    first_name: string;
    last_name: string;
  };
}

export default function DirectoryDetail() {
  const { id: listingId } = useParams<{ id: string }>();
  const [listing, setListing] = useState<Listing | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: '',
    comment: ''
  });
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const { user, profile } = useAuth();

  useEffect(() => {
    if (listingId) {
      loadListing();
      loadReviews();
      checkFavoriteStatus();
    }
  }, [listingId, user]);

  const loadListing = async () => {
    try {
      const data = await dbHelpers.getListing(listingId!);
      setListing(data);
      
      // Track view
      await dbHelpers.trackEvent('listing_view', listingId!, user?.id);
    } catch (error) {
      console.error('Error loading listing:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async () => {
    try {
      const data = await dbHelpers.getReviews(listingId!);
      setReviews(data);
    } catch (error) {
      console.error('Error loading reviews:', error);
    }
  };

  const checkFavoriteStatus = async () => {
    if (!user) return;
    try {
      const favorites = await dbHelpers.getFavorites(user.id);
      setIsFavorite(favorites.some((fav: any) => fav.listing_id === listingId));
    } catch (error) {
      console.error('Error checking favorite:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      alert('Please sign in to save favorites');
      return;
    }

    try {
      if (isFavorite) {
        await dbHelpers.removeFavorite(user.id, listingId!);
        setIsFavorite(false);
      } else {
        await dbHelpers.addFavorite(user.id, listingId!);
        setIsFavorite(true);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      alert('Failed to update favorite');
    }
  };

  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      alert('Please sign in to leave a review');
      return;
    }

    setSubmitStatus('loading');

    try {
      await dbHelpers.createReview(
        listingId!,
        user.id,
        reviewForm.rating,
        reviewForm.title,
        reviewForm.comment
      );

      setSubmitStatus('success');
      setShowReviewForm(false);
      setReviewForm({ rating: 5, title: '', comment: '' });
      
      // Reload reviews
      setTimeout(() => {
        loadReviews();
        setSubmitStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Error submitting review:', error);
      setSubmitStatus('error');
    }
  };

  const handleQuoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('loading');

    try {
      await dbHelpers.createQuoteRequest(
        listingId!,
        quoteForm.name,
        quoteForm.email,
        quoteForm.phone,
        quoteForm.message,
        user?.id
      );

      setSubmitStatus('success');
      setQuoteForm({ name: '', email: '', phone: '', message: '' });
      
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } catch (error) {
      console.error('Error submitting quote:', error);
      setSubmitStatus('error');
    }
  };

  const handleWebsiteClick = async () => {
    if (!listing) return;

    try {
      // Track affiliate click
      const { data: user } = await supabase.auth.getUser();
      await dbHelpers.trackAffiliateClick(
        listing.id,
        user?.user?.id,
        {
          source: 'directory_detail',
          timestamp: new Date().toISOString()
        }
      );

      // Use affiliate link if available, otherwise use regular website
      const targetUrl = listing.affiliate_link || listing.website;
      if (targetUrl) {
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error tracking affiliate click:', error);
      // Still open the website even if tracking fails
      const targetUrl = listing.affiliate_link || listing.website;
      if (targetUrl) {
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading listing...</p>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Navbar scrolled={true} />
        <div className="container mx-auto px-6 py-24 text-center">
          <h1 className="text-3xl font-bold text-[#0A381F] mb-4">Listing Not Found</h1>
          <Link to="/directory" className="text-[#D4AF37] hover:underline cursor-pointer">
            Back to Directory
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Vendor Upgrade CTA - Only show if user owns this listing */}
      {user && listing?.user_id === user.id && !listing?.featured && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-rocket-line text-2xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Boost Your Visibility & Get 3x More Leads</h3>
                  <p className="text-sm text-white/90">Upgrade to Featured and appear at the top of directory searches</p>
                </div>
              </div>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/pricing')}
                className="px-6 py-2.5 bg-white text-orange-600 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pt-24 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="container mx-auto px-6 py-24">
            {/* Breadcrumb */}
            <div className="mb-6 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#D4AF37] cursor-pointer">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/directory" className="hover:text-[#D4AF37] cursor-pointer">Directory</Link>
              <span className="mx-2">/</span>
              <span className="text-[#0A381F]">{listing.business_name}</span>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-8">
                {/* Header */}
                <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h1 className="text-3xl font-bold text-[#0A381F]">{listing.business_name}</h1>
                        {listing.verified && (
                          <span className="bg-[#D4AF37] text-white px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap flex items-center gap-1">
                            <i className="ri-verified-badge-fill"></i>
                            Verified
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                        <span className="flex items-center gap-1">
                          <i className="ri-map-pin-line"></i>
                          {listing.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-price-tag-3-line"></i>
                          {listing.price_range}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <i
                              key={star}
                              className={`${
                                star <= listing.rating ? 'ri-star-fill text-[#D4AF37]' : 'ri-star-line text-gray-300'
                              } text-lg`}
                            ></i>
                          ))}
                        </div>
                        <span className="text-sm text-gray-600">
                          {listing.rating.toFixed(1)} ({listing.review_count} reviews)
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={toggleFavorite}
                      className="w-12 h-12 flex items-center justify-center rounded-full border-2 border-gray-200 hover:border-[#D4AF37] transition-all cursor-pointer"
                    >
                      <i className={`${isFavorite ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-gray-400'} text-xl`}></i>
                    </button>
                  </div>

                  <img
                    src={listing.image_url}
                    alt={listing.business_name}
                    className="w-full h-80 object-cover rounded-xl mb-6"
                  />

                  <div className="flex gap-3">
                    {listing.website && (
                      <button
                        onClick={handleWebsiteClick}
                        className="flex-1 px-6 py-3 bg-[#D4AF37] text-white rounded-lg font-semibold hover:bg-[#C19B2F] transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                      >
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-external-link-line text-lg"></i>
                        </div>
                        Visit Website
                        {listing.has_affiliate_program && listing.affiliate_status === 'active' && (
                          <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Partner</span>
                        )}
                      </button>
                    )}
                    <a
                      href={`tel:${listing.phone}`}
                      className="flex-1 bg-[#0A381F] hover:bg-[#0D4A28] text-white py-3 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      <i className="ri-phone-line"></i>
                      Call Now
                    </a>
                  </div>
                </div>

                {/* About */}
                <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                  <h2 className="text-2xl font-bold text-[#0A381F] mb-4">About</h2>
                  <p className="text-gray-700 leading-relaxed mb-6">{listing.description}</p>

                  <h3 className="text-xl font-bold text-[#0A381F] mb-3">Services Offered</h3>
                  <div className="flex flex-wrap gap-2">
                    {listing.services.map((service, index) => (
                      <span
                        key={index}
                        className="bg-[#F5F5F0] text-[#0A381F] px-4 py-2 rounded-lg text-sm font-medium"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Reviews */}
                <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-[#0A381F]">Reviews ({reviews.length})</h2>
                    {user && (
                      <button
                        onClick={() => setShowReviewForm(!showReviewForm)}
                        className="bg-[#D4AF37] hover:bg-[#C19B2F] text-white px-6 py-2 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap"
                      >
                        Write Review
                      </button>
                    )}
                  </div>

                  {/* Review Form */}
                  {showReviewForm && (
                    <form onSubmit={handleReviewSubmit} className="mb-8 p-6 bg-[#F5F5F0] rounded-xl">
                      <h3 className="text-lg font-bold text-[#0A381F] mb-4">Share Your Experience</h3>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-semibold text-[#0A381F] mb-2">Rating</label>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                              className="cursor-pointer"
                            >
                              <i
                                className={`${
                                  star <= reviewForm.rating ? 'ri-star-fill text-[#D4AF37]' : 'ri-star-line text-gray-300'
                                } text-2xl`}
                              ></i>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-semibold text-[#0A381F] mb-2">Review Title</label>
                        <input
                          type="text"
                          value={reviewForm.title}
                          onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                          placeholder="Sum up your experience"
                          required
                        />
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-semibold text-[#0A381F] mb-2">Your Review</label>
                        <textarea
                          value={reviewForm.comment}
                          onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] h-32"
                          placeholder="Tell others about your experience..."
                          required
                          maxLength={500}
                        ></textarea>
                        <p className="text-xs text-gray-500 mt-1">{reviewForm.comment.length}/500 characters</p>
                      </div>

                      <div className="flex gap-3">
                        <button
                          type="submit"
                          disabled={submitStatus === 'loading'}
                          className="bg-[#D4AF37] hover:bg-[#C19B2F] text-white px-6 py-2 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          {submitStatus === 'loading' ? 'Submitting...' : 'Submit Review'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowReviewForm(false)}
                          className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap"
                        >
                          Cancel
                        </button>
                      </div>

                      {submitStatus === 'success' && (
                        <p className="mt-4 text-green-600 text-sm">Review submitted! It will appear after moderation.</p>
                      )}
                      {submitStatus === 'error' && (
                        <p className="mt-4 text-red-600 text-sm">Failed to submit review. Please try again.</p>
                      )}
                    </form>
                  )}

                  {/* Reviews List */}
                  <div className="space-y-6">
                    {reviews.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No reviews yet. Be the first to review!</p>
                    ) : (
                      reviews.map((review) => (
                        <div key={review.id} className="border-b border-gray-200 pb-6 last:border-0">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-[#0A381F]">
                                  {review.profiles.first_name} {review.profiles.last_name}
                                </span>
                                <div className="flex items-center">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <i
                                      key={star}
                                      className={`${
                                        star <= review.rating ? 'ri-star-fill text-[#D4AF37]' : 'ri-star-line text-gray-300'
                                      } text-sm`}
                                    ></i>
                                  ))}
                                </div>
                              </div>
                              <p className="text-sm text-gray-500">
                                {new Date(review.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                          </div>
                          <h4 className="font-semibold text-[#0A381F] mb-2">{review.title}</h4>
                          <p className="text-gray-700">{review.comment}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Contact Info */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 sticky top-24">
                  <h3 className="text-xl font-bold text-[#0A381F] mb-4">Contact Information</h3>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex items-start gap-3">
                      <i className="ri-map-pin-line text-[#D4AF37] text-xl w-6 h-6 flex items-center justify-center"></i>
                      <div>
                        <p className="text-sm font-semibold text-[#0A381F]">Address</p>
                        <p className="text-sm text-gray-600">{listing.address}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-phone-line text-[#D4AF37] text-xl w-6 h-6 flex items-center justify-center"></i>
                      <div>
                        <p className="text-sm font-semibold text-[#0A381F]">Phone</p>
                        <a href={`tel:${listing.phone}`} className="text-sm text-gray-600 hover:text-[#D4AF37] cursor-pointer">
                          {listing.phone}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-mail-line text-[#D4AF37] text-xl w-6 h-6 flex items-center justify-center"></i>
                      <div>
                        <p className="text-sm font-semibold text-[#0A381F]">Email</p>
                        <a href={`mailto:${listing.email}`} className="text-sm text-gray-600 hover:text-[#D4AF37] cursor-pointer break-all">
                          {listing.email}
                        </a>
                      </div>
                    </div>

                    {listing.website && (
                      <div className="flex items-start gap-3">
                        <i className="ri-global-line text-[#D4AF37] text-xl w-6 h-6 flex items-center justify-center"></i>
                        <div>
                          <p className="text-sm font-semibold text-[#0A381F]">Website</p>
                          <button
                            onClick={handleWebsiteClick}
                            className="text-sm text-gray-600 hover:text-[#D4AF37] cursor-pointer break-all text-left"
                          >
                            {listing.website}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Quote Form */}
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-bold text-[#0A381F] mb-4">Request a Quote</h4>
                    <form onSubmit={handleQuoteSubmit} className="space-y-4">
                      <input
                        type="text"
                        value={quoteForm.name}
                        onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                        placeholder="Your Name"
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm"
                        required
                      />
                      <input
                        type="email"
                        value={quoteForm.email}
                        onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                        placeholder="Your Email"
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm"
                        required
                      />
                      <input
                        type="tel"
                        value={quoteForm.phone}
                        onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                        placeholder="Your Phone"
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm"
                        required
                      />
                      <textarea
                        value={quoteForm.message}
                        onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })}
                        placeholder="Tell us about your needs..."
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm h-24"
                        required
                        maxLength={500}
                      ></textarea>
                      <button
                        type="submit"
                        disabled={submitStatus === 'loading'}
                        className="w-full bg-[#D4AF37] hover:bg-[#C19B2F] text-white py-3 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        {submitStatus === 'loading' ? 'Sending...' : 'Send Request'}
                      </button>
                      {submitStatus === 'success' && (
                        <p className="text-green-600 text-sm text-center">Quote request sent successfully!</p>
                      )}
                      {submitStatus === 'error' && (
                        <p className="text-red-600 text-sm text-center">Failed to send request. Please try again.</p>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
